import { LoaderFunction } from 'react-router-dom';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import {
  buildRegisteredByOperators,
  resolveRegisteredBy,
} from '../../../../../utils/registeredByOperators';
import { client } from '../../../../../gql/client';
import { FETCH_USERS } from '../../../../../gql/queries/users';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';
import { GET_ACTION_BY_ID } from '../../../../../gql/queries/actions/actions';
import { isBasicAction } from '../../../../../utils/order';
import {
  BASIC_SCHEDULE_BASELINE_KEY,
  type BasicSchedule,
  resolveBasicSchedule,
} from '../../../../../components/forms/ActionForms/ActionEdition/basicSchedule';

// Constants
import { formActionNaming } from '../../../../../components/forms/ActionForms/constants';

// Selects
import {
  selectAssetsAndActivitys,
  selectActiveOperatorsAndVehicles,
} from '../../../../../gql/selectors/actions';

// Types
import { AuthContextType } from '../../../../../hooks/useAuth';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import {
  ACTION_CREATION_SCOPE,
  ACTION_MEDIA_SCOPE,
  readCreationDraftId,
} from '../../../../../utils/draftScopes';
import { foreignCreationParentRedirect } from '../creationDraft';

// Operarios para el dropdown "Registrado por" del paso Edición (mismo origen
// que NewAction / ActionsValidation: operarios activos del tenant + el usuario
// logueado si no es Tablet). Sin esto el dropdown salía vacío ("No se
// encontraron resultados") en la Actuación básica del RV —que usa ESTE loader,
// no NewActionLoader— feedback usuario 2026-07-24.
const loadRegisteredByOperators = async (userData: AuthContextType | null) => {
  try {
    const { operators } = await selectActiveOperatorsAndVehicles(true);
    return buildRegisteredByOperators(operators, userData);
  } catch {
    return buildRegisteredByOperators([], userData);
  }
};

// Usuarios ya cacheados por `selectActiveOperatorsAndVehicles` (FetchUsers):
// sirven para reconocer a una persona que ya no está en la lista de activos.
const readKnownUsers = () => {
  try {
    return (
      client.readQuery<{ users?: { id: string; name?: string; surName?: string; rolActive?: string | null }[] }>({
        query: FETCH_USERS,
      })?.users ?? []
    );
  } catch {
    return [];
  }
};

/**
 * Horario de la básica de la CACHÉ: `createBasicAction` acaba de dejar la
 * Order (inicio y fin, ya releída tras vincular) y la Action (llegada). Sin
 * red y sin caché (la básica aún encolada), vacío.
 */
const readCachedBasicSchedule = (
  orderId: string | undefined,
  actionId: string | undefined
): BasicSchedule => {
  const read = <T,>(fn: () => T | null | undefined): T | null => {
    try {
      return fn() ?? null;
    } catch {
      return null;
    }
  };
  const order = orderId
    ? read(
        () =>
          client.readQuery<{
            order?: { registerInitDate?: string; registerEndDate?: string };
          }>({ query: GET_ORDER_BY_ID, variables: { id: orderId } })?.order
      )
    : null;
  const action = actionId
    ? read(
        () =>
          client.readQuery<{ action?: { registerArrivalDate?: string } }>({
            query: GET_ACTION_BY_ID,
            variables: { id: actionId },
          })?.action
      )
    : null;
  return {
    registerInitDate: order?.registerInitDate ?? '',
    registerArrivalDate: action?.registerArrivalDate ?? '',
    registerEndDate: order?.registerEndDate ?? '',
  };
};

export const BuildingLoader =
  (userData: AuthContextType | null): LoaderFunction =>
  async ({ params, request }) => {
  // Un borrador de OTRO padre no se abre bajo esta URL: atrás, una URL
  // compartida o el «Editar» de un resumen viejo enseñaban — y guardaban — el
  // borrador de la OT Y bajo la de la X (auditoría de creación 2.3).
  const foreignParent = foreignCreationParentRedirect({ params, request });
  if (foreignParent) return foreignParent;
  // Las copias de las pestañas, la lista de fotos y el croquis son del
  // borrador en curso (`draftId`): si eran de otro, fuera, antes de que nadie
  // las lea. Síncrono y antes de cualquier `await`: el loader de `details` y el
  // de la pestaña corren en paralelo (spec 2026-11-02 wizard-draft-ownership).
  const draftId = readCreationDraftId();
  claimDraftScope(ACTION_CREATION_SCOPE, draftId);
  claimDraftScope(ACTION_MEDIA_SCOPE, draftId);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    // Stringify data
    const stringifyForm = sessionStorage.getItem(
      formActionNaming.NEW_EDITION_FORM
    );

    const workOrderStringifyForm = sessionStorage.getItem(
      formActionNaming.NEW_ACTION_DATA
    );

    // Variables to assign
    let sessionStorageData;
    let workOrderData;

    // Variables assignment
    if (stringifyForm) {
      sessionStorageData = JSON.parse(decompressSessionStorage(stringifyForm));
    }

    if (workOrderStringifyForm) {
      workOrderData = JSON.parse(
        decompressSessionStorage(workOrderStringifyForm)
      );
    }

    const dataAssetsAndActivitys = await selectAssetsAndActivitys();
    const registeredByOperators = await loadRegisteredByOperators(userData);

    // «Registrado por» solo arranca con un valor ELEGIBLE (2026-09-04). La
    // básica llega sembrada con la cuenta de la tablet, que no es persona y
    // no está en las opciones: sin esto el desplegable salía vacío pero la
    // validación pasaba, y nadie elegía a quién registró de verdad. Un
    // operario dado de baja sí se conserva (y se enseña).
    const registeredBy = resolveRegisteredBy(
      sessionStorageData?.registeredByUserId ??
        workOrderData?.registeredByUserId,
      registeredByOperators,
      readKnownUsers()
    );

    // Horario de la Actuació bàsica (plan 2026-10-08): lo calcula el back con
    // el hueco del tramo y quien hace la básica (también la tablet) lo puede
    // corregir aquí (12-oct). La sesión de la pestaña solo manda en lo que el
    // usuario tocó (misma regla que la validación, R1); la base se guarda en
    // el formulario para que el envío mande solo lo cambiado.
    const isBasic = isBasicAction({
      containerType: workOrderData?.containerType,
    });
    const freshSchedule = isBasic
      ? readCachedBasicSchedule(workOrderData?.orderId, workOrderData?.actionId)
      : null;

    const combinedData = {
      ...workOrderData,
      ...dataAssetsAndActivitys,
      ...sessionStorageData,
      ...(freshSchedule
        ? {
            ...resolveBasicSchedule(
              freshSchedule,
              sessionStorageData,
              BASIC_SCHEDULE_BASELINE_KEY
            ),
            [BASIC_SCHEDULE_BASELINE_KEY]: freshSchedule,
          }
        : {}),
      isBasicAction: isBasic,
      operators: registeredBy.options,
      registeredByUserId: registeredBy.value,
      breadCrumbsData: {
        orderParentId: workOrderData?.orderParentId,
        orderParentRoid: workOrderData?.orderParentRoid,
      },
    };

    return combinedData;
  };

  return { data: load() };
};
